import { useMemo } from 'react';
import { filterObjectKeys } from '~/utils/Object';
import { useAppSelector } from '../useAppSelector';
import { useUserSelector } from '../useUserSelector';

/**
 * Hook to get the user's primary specialty.
 *
 * Priority logic:
 * 1. First, checks `state.userCredentials.specialty` from Redux
 * 2. Falls back to computing from `currentUser.office_info.education.specialtiesAndProcedures`
 *
 * This hook automatically handles DSO (multi-office) scenarios via `useUserSelector`.
 *
 * @returns {string | undefined} The specialty key (e.g., "specialty_General", "specialty_Pediatric") or undefined if no specialty found
 */
export const useUserSpecialty = (): string | undefined => {
  const currentUser = useUserSelector();
  const userCredentials = useAppSelector((state) => state.userCredentials);

  const specialty = useMemo(() => {
    // Priority 1: Try userCredentials.specialty first (computed in reducer)
    if (userCredentials?.specialty) {
      return userCredentials.specialty;
    }

    // Priority 2: Fallback to computing from office_info education
    const specialtiesAndProcedures = currentUser?.office_info?.education?.specialtiesAndProcedures;

    if (!specialtiesAndProcedures) {
      return undefined;
    }

    const filteredSpecialties = filterObjectKeys(specialtiesAndProcedures, 'specialty');
    const firstSpecialty = Object.keys(filteredSpecialties || {})?.[0];

    return firstSpecialty;
  }, [currentUser?.office_info?.education?.specialtiesAndProcedures, userCredentials?.specialty]);

  return specialty;
};
